import {
    type MRT_ColumnDef,
    useMaterialReactTable,
    MRT_RowData,
    MaterialReactTable,
} from 'material-react-table'
import { ReactElement } from 'react'

interface ISimpleMRTTable<DataType extends MRT_RowData> {
    data: DataType[]
    columns: MRT_ColumnDef<DataType>[]
    tableHeight?: string
    isLoading?: boolean
    isCurvedBottomToolbar?: boolean
    enableRowActions?: boolean
    renderRowActions?: (props: { row: any }) => React.ReactNode
}

const SimpleMRTTable = <DataType extends MRT_RowData>(
    props: ISimpleMRTTable<DataType>,
): ReactElement => {
    const {
        data,
        columns,
        tableHeight,
        isLoading = false,
        isCurvedBottomToolbar,
        enableRowActions = false,
        renderRowActions: customRenderRowActions,
    } = props

    const table = useMaterialReactTable({
        columns,
        data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
        enableColumnActions: false,
        enableColumnFilters: false,
        enablePagination: false,
        enableSorting: false,
        enableStickyHeader: true,
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.background.default, //change default background color
        }),
        muiTableHeadProps: {
            sx: {
                th: {
                    // borderBottom: `1px solid ${theme.palette.primary.main}`,
                    '&:first-of-type': {
                        paddingLeft: '15px',
                    },
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                borderBottomLeftRadius: isCurvedBottomToolbar ? '20px' : '0px',
                borderBottomRightRadius: isCurvedBottomToolbar ? '20px' : '0px',
            },
        },
        muiTableBodyRowProps: {
            sx: {
                '&:nth-child(odd)': {
                    backgroundColor: '#E9F6FF !important',
                },
            },
        },
        muiTableProps: {
            sx: {
                caption: {
                    captionSide: 'top',
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                backgroundColor: '#E9F6FF',
                borderBottom: '3px solid #FFFFFF',
                borderTop: '3px solid #FFFFFF',
                '&:first-of-type': {
                    borderLeft: '7px solid #FFFFFF',
                    paddingLeft: '11px',
                },
                '&:last-child': {
                    borderRight: '6px solid #FFFFFF',
                },
            },
        },
        muiTableContainerProps: {
            sx: {
                flex: '1 1 0',
                maxHeight: tableHeight ? tableHeight : '500px',
                overflow: 'auto',
                '&: hover': {
                    overflow: 'auto',
                },
            },
        },
        muiBottomToolbarProps: {
            sx: {
                minHeight: '0px',
            },
        },
        // renderEmptyRowsFallback: ({ table }) => (
        //     <Typography sx={{ textAlign: 'center' }}>
        //         Customized No Rows Overlay
        //     </Typography>
        // ),
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableRowVirtualization: true,
        rowVirtualizerOptions: { overscan: 5 },
        state: {
            density: 'compact',
            isLoading,
        },
        enableRowActions,
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: '', // This will remove the header text
            },
        },
        renderRowActions: enableRowActions ? customRenderRowActions : undefined,
    })

    return <MaterialReactTable table={table} />
}

export default SimpleMRTTable
